<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import { parseValidationErrors } from '../../composables/useFlowEditor';

const props = defineProps({
  flow: { type: Object, required: true },
  isFavorite: { type: Boolean, default: false },
  accountId: { type: Number, required: true },
});

defineEmits(['toggleFavorite', 'delete', 'dragstart', 'dragend']);

const { t } = useI18n();
const store = useStore();

const CATEGORY_COLORS = {
  management: 'bg-n-blue-2 text-n-blue-11',
  follow_up: 'bg-n-iris-2 text-n-iris-11',
  nurturing: 'bg-n-amber-2 text-n-amber-11',
  reminders: 'bg-n-teal-2 text-n-teal-11',
};

const categoryChipClass = computed(() => {
  const cat = props.flow.category;
  return cat ? (CATEGORY_COLORS[cat] ?? '') : '';
});

const categoryLabel = computed(() => {
  const cat = props.flow.category;
  if (!cat) return null;
  return t(`FLOWS.CATEGORY.${cat.toUpperCase()}`);
});
const router = useRouter();

const titleEl = ref(null);
const isDraggable = ref(true);

const isToggling = ref(false);
const isActive = computed(() => props.flow.status === 'active');

async function toggleActive() {
  if (isToggling.value) return;
  isToggling.value = true;
  try {
    if (isActive.value) {
      await store.dispatch('flows/pause', props.flow.id);
      useAlert(t('FLOWS.PAUSE.SUCCESS'));
    } else {
      await store.dispatch('flows/publish', props.flow.id);
      useAlert(t('FLOWS.PUBLISH.SUCCESS'));
    }
  } catch (e) {
    const nodes = props.flow.draft_version?.definition?.nodes ?? [];
    const apiErrors = e.apiErrors?.length ? e.apiErrors : [e.message];
    const parsed = parseValidationErrors(apiErrors, nodes, t);
    const first = parsed[0]?.message ?? t('FLOWS.PUBLISH.ERROR');
    const rest = parsed.length - 1;
    const msg =
      rest > 0
        ? `${first} ${t('FLOWS.PUBLISH.AND_MORE_ERRORS', { count: rest })}`
        : first;
    useAlert(msg);
  } finally {
    isToggling.value = false;
  }
}

function formatDate(iso) {
  if (!iso) return '';
  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(iso));
}

function openEditor() {
  router.push({
    name: 'flows_editor',
    params: { accountId: props.accountId, flowId: props.flow.id },
  });
}

onMounted(() => {
  if (titleEl.value) titleEl.value.textContent = props.flow.name;
});

watch(
  () => props.flow.name,
  name => {
    if (titleEl.value && document.activeElement !== titleEl.value) {
      titleEl.value.textContent = name;
    }
  }
);

async function saveTitle() {
  isDraggable.value = true;
  const name = titleEl.value?.textContent?.trim();
  if (!name) {
    titleEl.value.textContent = props.flow.name;
    return;
  }
  if (name === props.flow.name) return;
  await store.dispatch('flows/update', { id: props.flow.id, name });
}

function onTitleKeydown(e) {
  if (e.key === 'Enter') {
    e.preventDefault();
    titleEl.value?.blur();
  }
  if (e.key === 'Escape') {
    titleEl.value.textContent = props.flow.name;
    titleEl.value?.blur();
  }
}

function onTitleMousedown(e) {
  e.stopPropagation();
  isDraggable.value = false;
}

function onTitleFocus() {
  isDraggable.value = false;
}
</script>

<template>
  <div
    class="flex items-center gap-3 rounded-xl border border-n-weak bg-n-background px-4 py-3 hover:bg-n-alpha-1 transition-colors group"
    :class="isDraggable ? 'cursor-grab active:cursor-grabbing' : ''"
    :draggable="isDraggable"
    @click="openEditor"
    @dragstart="isDraggable && $emit('dragstart', $event)"
    @dragend="$emit('dragend', $event)"
  >
    <button
      type="button"
      :aria-label="isFavorite ? 'Remove from favorites' : 'Add to favorites'"
      class="text-n-slate-10 hover:text-n-amber-10 flex-shrink-0"
      @click.stop="$emit('toggleFavorite', flow.id)"
    >
      <i
        class="text-base"
        :class="
          isFavorite
            ? 'i-ri-star-fill text-n-amber-10'
            : 'i-ri-star-line text-n-slate-10'
        "
      />
    </button>

    <div class="flex-1 min-w-0">
      <div class="flex items-center gap-2">
        <p
          ref="titleEl"
          contenteditable="true"
          spellcheck="false"
          class="text-sm font-medium text-n-slate-12 truncate focus:outline-none cursor-text"
          @mousedown="onTitleMousedown"
          @focus="onTitleFocus"
          @blur="saveTitle"
          @keydown="onTitleKeydown"
          @click.stop
        />
        <span
          v-if="categoryLabel"
          class="flex-shrink-0 rounded-full px-2 py-0.5 text-xs font-medium"
          :class="categoryChipClass"
        >
          {{ categoryLabel }}
        </span>
      </div>
      <p class="text-xs text-n-slate-10 truncate mt-0.5">
        <span v-if="flow.flow_folder_id">
          <span class="i-lucide-folder size-3 inline-block mr-1" />
          {{ flow.folder_name ?? '' }}
          <span class="mx-1">·</span>
        </span>
        {{ $t('FLOWS.CARD.UPDATED') }}: {{ formatDate(flow.updated_at) }}
        <span class="mx-1">·</span>
        {{ $t('FLOWS.CARD.CREATED') }}: {{ formatDate(flow.created_at) }}
      </p>
    </div>

    <button
      type="button"
      :aria-label="
        isActive ? $t('FLOWS.STATUS.PAUSE') : $t('FLOWS.STATUS.ACTIVATE')
      "
      class="flex-shrink-0 relative inline-flex h-5 w-9 cursor-pointer items-center rounded-full transition-colors focus:outline-none disabled:opacity-60"
      :class="isActive ? 'bg-n-teal-9' : 'bg-n-ruby-9'"
      :disabled="isToggling"
      @click.prevent.stop="toggleActive"
    >
      <span
        class="pointer-events-none absolute left-0.5 inline-block size-4 rounded-full bg-white shadow transition-transform"
        :class="isActive ? 'translate-x-4' : 'translate-x-0'"
      />
    </button>

    <div
      class="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0"
    >
      <button
        type="button"
        :aria-label="$t('FLOWS.EDITOR.TITLE')"
        class="rounded p-1 text-n-slate-10 hover:text-n-slate-12 hover:bg-n-alpha-1"
        @click.stop="openEditor"
      >
        <span class="i-lucide-pencil size-4" />
      </button>
      <button
        type="button"
        :aria-label="$t('FLOWS.DELETE.CONFIRM_TITLE')"
        class="rounded p-1 text-n-slate-10 hover:text-n-ruby-11 hover:bg-n-alpha-1"
        @click.stop="$emit('delete', flow)"
      >
        <span class="i-lucide-trash-2 size-4" />
      </button>
    </div>
  </div>
</template>
